import React, { useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { usersAPI, appointmentsAPI } from '../../services/api';

interface Step1BasicInfoProps {
  onNext: (data: any) => void;
  initialData?: any;
  onAutoSave?: (appointmentId: number) => void; // callback برای اطلاع از auto-save
}

interface FormData {
  full_name: string;
  phone: string;
}

const Step1BasicInfo: React.FC<Step1BasicInfoProps> = ({ onNext, initialData, onAutoSave }) => {
  const { register, handleSubmit, formState: { errors }, watch } = useForm<FormData>({
    defaultValues: initialData
  });

  const watchedData = watch();
  const autoSaveTimerRef = useRef<NodeJS.Timeout | null>(null);
  const savedAppointmentIdRef = useRef<number | null>(null);
  const savedUserIdRef = useRef<number | null>(null);

  // Auto-save وقتی کاربر اطلاعات را وارد می‌کند
  useEffect(() => {
    const fullName = watchedData.full_name;
    const phone = watchedData.phone;

    // بررسی اینکه آیا اطلاعات کامل و معتبر است
    const isValid = fullName && fullName.trim().length > 0 &&
      phone && /^09\d{9}$/.test(phone);

    if (!isValid) {
      // اگر اطلاعات معتبر نیست، تایمر را پاک کن
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
        autoSaveTimerRef.current = null;
      }
      return;
    }

    // پاک کردن تایمر قبلی
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
    }

    // تنظیم تایمر جدید برای auto-save بعد از 3 ثانیه
    autoSaveTimerRef.current = setTimeout(async () => {
      try {
        // دریافت یا ایجاد کاربر
        const userResponse = await usersAPI.getOrCreate({
          full_name: fullName.trim(),
          phone: phone.trim()
        });

        const userId = userResponse.data.id;
        savedUserIdRef.current = userId;

        // اگر قبلاً appointment ایجاد نشده، ایجاد کن
        if (!savedAppointmentIdRef.current) {
          const appointmentResponse = await appointmentsAPI.create({
            user_id: userId,
            service_id: undefined // بدون service_id چون هنوز انتخاب نشده
          });

          const appointmentId = appointmentResponse.data.id;
          savedAppointmentIdRef.current = appointmentId;

          // اطلاع دادن به parent component
          if (onAutoSave) {
            onAutoSave(appointmentId);
          }
        }
      } catch (error) {
        console.error('Error in auto-save:', error);
        // در صورت خطا، ساکت بمان تا UX را خراب نکند
      }
    }, 3000); // 3 ثانیه بعد از آخرین تغییر

    // Cleanup
    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
    };
  }, [watchedData.full_name, watchedData.phone, onAutoSave]);

  const onSubmit = async (data: FormData) => {
    // اگر قبلاً appointment ایجاد شده، از همان استفاده کن
    if (savedAppointmentIdRef.current && savedUserIdRef.current) {
      // به‌روزرسانی user_id در data
      data = { ...data, user_id: savedUserIdRef.current, appointment_id: savedAppointmentIdRef.current } as any;
    }
    onNext(data);
  };

  const primaryButtonClass = 'inline-flex items-center justify-center rounded-lg bg-gray-900 text-white py-3 px-4 text-sm font-medium transition-colors hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-gray-300 disabled:opacity-70 disabled:cursor-not-allowed';

  return (
    <div className="w-full max-w-sm mx-auto">
      <div className="bg-white rounded-2xl p-0 sm:p-6 shadow-none sm:shadow-sm sm:border sm:border-gray-200">
        <h2 className="text-lg font-semibold text-gray-900 mb-6 text-center">نوبت‌دهی</h2>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">نام و نام خانوادگی</label>
            <input
              type="text"
              {...register('full_name', { required: 'نام و نام خانوادگی الزامی است' })}
              className="w-full rounded-lg border border-gray-300 px-3 py-3 sm:py-2 text-base sm:text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:border-gray-400"
              placeholder="نام و نام خانوادگی"
            />
            {errors.full_name && (
              <p className="text-xs text-red-500">{errors.full_name.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">شماره تماس</label>
            <input
              type="tel"
              {...register('phone', {
                required: 'شماره تماس الزامی است',
                pattern: {
                  value: /^09\d{9}$/,
                  message: 'شماره تماس باید با 09 شروع شود و 11 رقم باشد'
                }
              })}
              className="w-full rounded-lg border border-gray-300 px-3 py-3 sm:py-2 text-base sm:text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:border-gray-400"
              placeholder="09123456789"
              inputMode="numeric"
              dir="ltr"
            />
            {errors.phone && (
              <p className="text-xs text-red-500">{errors.phone.message}</p>
            )}
          </div>

          <button
            type="submit"
            className={`${primaryButtonClass} w-full py-3.5 sm:py-3`}
          >
            مرحله بعد
          </button>
        </form>
      </div>
    </div>
  );
};

export default Step1BasicInfo;
